import React from "react";
import { constants } from "@/constants.js";
import { urlChatID } from "@/utils/url.js";
import "rc-slider/assets/index.css";
import { Highlight, themes } from "prism-react-renderer";
import { useQuery, useQueryClient } from "react-query";
import { queryKey } from "@/store/queryKeys";
import { snakeToCamelObj } from "@/utils/utils";
import { ChatSettings } from "@/pages/chatbot/settings/ChatBotSettings";
import Accordion from "@/components/ui/Accordion";
import {Icon} from "@/components/Icon.tsx";
import Card from "@/components/ui/Card.jsx";

// @ts-ignore
import WixIntegration from "@/assets/images/wix-integrationpng.png";
import {useTranslation} from "react-i18next";

const codeBlock1 = (domain: string, token: string) => {
  return `<iframe
    src="${domain}/chat-iframe?token=${token}"
    width="100%"
    height="650"
    frameborder="0"
></iframe>`;
};
const codeBlock2 = (domain: string, token: string) => {
  return `<script
    id="wizychat-embed"
    type="module"
    src="${domain}/chat-embed.min.js"
    data-token="${token}">
</script>`;
};

const theme = themes.oceanicNext;
const ChatBotEmbed = () => {
    const { t } = useTranslation();
  const queryKeyID = `${queryKey.chatbotInterfaceSettings}-${urlChatID()}`;
  let chatbotQuery = useQuery({
    queryKey: queryKeyID,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      let res = await fetch(
        `${constants.url.API_PREFIX}chatbot/${urlChatID()}`,
      );
      return res.json();
    },
  });

  const [viewSettings, setSettings] = React.useState(
    null as null | ChatSettings,
  );
  const [viewChatbot, setChatbot] = React.useState(null as any);

  if (chatbotQuery.isFetched) {
    let data: any = snakeToCamelObj(chatbotQuery.data);
    if (!data.settings) {
      return <div>Loading...</div>;
    }
    if (!viewSettings) {
      setSettings(data.settings as ChatSettings);
    }
    if (!viewChatbot) {
      setChatbot(data);
    }
  }

  if (
    !viewSettings ||
    !viewChatbot ||
    chatbotQuery.isLoading ||
    !chatbotQuery.isSuccess
  ) {
    return <div>Loading...</div>;
  }

  const codeRenderer = ({
    className,
    style,
    tokens,
    getLineProps,
    getTokenProps,
  }: any) => (
    <pre style={style}>
      <div className="p-3">
        {tokens.map((line: any, i: number) => (
          <div key={i} {...getLineProps({ line })}>
            <span className="mr-5 select-none">{i + 1}</span>
            {line.map((token: any, key: number) => (
              <span key={key} {...getTokenProps({ token })} />
            ))}
          </div>
        ))}
      </div>
    </pre>
  );

  const items = [
    {
      title: (
        <div className="flex items-center">
          <Icon icon="fluent:comment-multiple-link-16-regular" width="32" />
          <span className="ml-2">{t("integrations.direct_link")}</span>
        </div>
      ),
      content: (
        <div>
          <div className="py-4">
            <a
              target="_blank"
              className="text-primary-500"
              href={`${constants.url.DOMAIN}/chat-iframe?token=${viewChatbot.token}`}
            >
              {`${constants.url.DOMAIN}/chat-iframe?token=${viewChatbot.token}`}
            </a>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <Icon icon="fluent:window-ad-20-filled" width="32" />
          <span className="ml-2">{t("integrations.iframe")}</span>
        </div>
      ),
      content: (
        <div>
          <div className="py-4">
            Add the code below anywhere on your website to embed the chat window
          </div>
          <Highlight
            theme={theme}
            code={codeBlock1(constants.url.DOMAIN, viewChatbot.token)}
            language="html"
          >
            {codeRenderer}
          </Highlight>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <Icon icon="fluent:chat-16-regular" width="32" />
          <span className="ml-2">
            <span className="ml-2">{t("integrations.bubble_button")}</span>
          </span>
        </div>
      ),
      content: (
        <div>
          <div className="py-4">
            {`Add <script> in the <head> block or in the <body> block`}
          </div>
          <Highlight
            theme={theme}
            code={codeBlock2(constants.url.DOMAIN, viewChatbot.token)}
            language="html"
          >
            {codeRenderer}
          </Highlight>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <Icon icon="ic:baseline-wordpress" width="32" />
          <span className="ml-2">{t("integrations.wordpress")}</span>
        </div>
      ),
      content: (
        <div>
          <div className="py-4">
            1){" "}
            <a
              target="_blank"
              className="text-primary-500"
              href="https://wizy.chat/assets/wp-wizychat.zip"
            >
              Download Plugin
            </a>
            <br />
            2) {'WordPress Admin -> Plugins -> Add New -> "Upload Plugin"'}
            <br />
            3) {'Select ZIP file and click "Install Now" -> "Activate Plugin"'}
            <br />
            4) Go to "WizyChat Settings" and save chatbot token:{" "}
            <b>{viewChatbot.token}</b>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <Icon icon="simple-icons:wix" width="36" />
          <span className="ml-2">{t("integrations.wix")}</span>
        </div>
      ),
      content: (
        <div>
          <div className="py-4">
            1) Enter "Edit mode"
            <br />
            2) Click "Add Elements"
            <br />
            3) Click "Embed code"
            <br />
            4) Click "Marketing Tools"
            <br />
            5) Click "Custom Code"
            <br />
            6) Paste the code below to a form:
            <br />
            <br />
            <Highlight
              theme={theme}
              code={codeBlock2(constants.url.DOMAIN, viewChatbot.token)}
              language="html"
            >
              {codeRenderer}
            </Highlight>
            <br />
            <br />
            <img src={WixIntegration} alt="wix-integration" />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Card title={t("integrations")}>
      <Accordion items={items} />
    </Card>
  );
};

export default ChatBotEmbed;
